import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchgastenverbruik } from "../Reducers/actions";
import moment from "moment";
import "moment/locale/nl";
import Chart from "react-apexcharts";

moment.locale('nl');

const GastenVerbruikDashboard = () => {
  const dispatch = useDispatch();
  const gastenverbruik = useSelector((state) => state.gastenverbruik.gastenverbruik);
  const user = useSelector((state) => state.auth.user);

  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });
  const [selectedGastenprofiel, setSelectedGastenprofiel] = useState('');
  const [gastenprofielen, setGastenprofielen] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    totalReservations: 0,
    totalKwh: 0,
    totalRevenue: 0,
    avgKwhPerReservation: 0,
    topStandplaatsen: [],
    recentActivity: [],
    kwhTrend: [],
    statusDistribution: {},
    monthlyData: [],
    weeklyData: []
  });

  useEffect(() => {
    loadDashboardData();
  }, [dispatch]); // Only load on initial mount

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const query = {
        aankomstVanaf: dateRange.startDate,
        vertrekTot: dateRange.endDate
      };

      await dispatch(fetchgastenverbruik(query));
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (gastenverbruik && gastenverbruik.length > 0) {
      // Extract unique gastenprofielen
      const uniqueProfielen = [...new Set(
        gastenverbruik
          .filter(item => item.gast?.gastenprofiel?.naam)
          .map(item => item.gast.gastenprofiel.naam)
      )].sort();
      setGastenprofielen(uniqueProfielen);

      calculateDashboardMetrics();
    }
  }, [gastenverbruik]);

  // Separate effect to recalculate metrics when gastenprofiel filter changes
  useEffect(() => {
    if (gastenverbruik && gastenverbruik.length > 0) {
      calculateDashboardMetrics();
    }
  }, [selectedGastenprofiel]);

  const calculateDashboardMetrics = () => {
    // Filter data by selected gastenprofiel
    const filteredData = selectedGastenprofiel
      ? gastenverbruik.filter(item => item.gast?.gastenprofiel?.naam === selectedGastenprofiel)
      : gastenverbruik;

    const totalReservations = filteredData.length;
    const totalKwh = filteredData.reduce((sum, item) => {
      // Calculate kWh as meterstandvertrek - meterstanndaankomst
      const kwh = (item.meterstandvertrek && item.meterstanndaankomst)
        ? (item.meterstandvertrek - item.meterstanndaankomst)
        : 0;
      return sum + kwh;
    }, 0);
    const totalRevenue = filteredData.reduce((sum, item) => {
      const kwh = (item.meterstandvertrek && item.meterstanndaankomst)
        ? (item.meterstandvertrek - item.meterstanndaankomst)
        : 0;

      // Get price per kWh from gastenprofiel or standplaats categorie
      const prijsPerKwh = item.gast?.gastenprofiel?.prijs ||
                         item.standplaats?.categorie?.prijs ||
                         item.accommodaties?.categorie?.prijs || 0;

      const revenue = kwh * prijsPerKwh;
      return sum + revenue;
    }, 0);
    const avgKwhPerReservation = totalReservations > 0 ? totalKwh / totalReservations : 0;

    // Status distribution
    const statusDistribution = filteredData.reduce((acc, item) => {
      const status = item.status || 'onbekend';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Top standplaatsen
    const standplaatsenCount = filteredData.reduce((acc, item) => {
      const plaatsNaam = item.standplaats?.naam || item.accommodaties?.naam || 'Onbekend';
      if (!acc[plaatsNaam]) {
        acc[plaatsNaam] = { count: 0, kwh: 0, revenue: 0 };
      }
      acc[plaatsNaam].count += 1;
      // Calculate kWh as meterstandvertrek - meterstanndaankomst
      const kwh = (item.meterstandvertrek && item.meterstanndaankomst)
        ? (item.meterstandvertrek - item.meterstanndaankomst)
        : 0;
      acc[plaatsNaam].kwh += kwh;

      // Calculate revenue: kWh * price per kWh
      const prijsPerKwh = item.gast?.gastenprofiel?.prijs ||
                         item.standplaats?.categorie?.prijs ||
                         item.accommodaties?.categorie?.prijs || 0;
      acc[plaatsNaam].revenue += kwh * prijsPerKwh;
      return acc;
    }, {});

    const topStandplaatsen = Object.entries(standplaatsenCount)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Recent activity (laatste 10 items)
    const recentActivity = filteredData
      .sort((a, b) => new Date(b.datum) - new Date(a.datum))
      .slice(0, 10)
      .map(item => ({
        id: item._id,
        gast: item.gast?.voornaam && item.gast?.achternaam
          ? `${item.gast.voornaam} ${item.gast.achternaam}`
          : 'Onbekende gast',
        keuze: item.standplaats?.naam || item.accommodaties?.naam || 'Onbekend',
        kwh: (item.meterstandvertrek && item.meterstanndaankomst)
          ? (item.meterstandvertrek - item.meterstanndaankomst)
          : 0,
        status: item.status,
        datum: item.datum
      }));

    // KWh trend (per dag in de geselecteerde periode)
    const kwhTrend = calculateKwhTrend(filteredData);

    // Monthly and weekly analysis
    const monthlyData = calculateMonthlyData(filteredData);
    const weeklyData = calculateWeeklyData(filteredData);

    setDashboardData({
      totalReservations,
      totalKwh,
      totalRevenue,
      avgKwhPerReservation,
      topStandplaatsen,
      recentActivity,
      kwhTrend,
      statusDistribution,
      monthlyData,
      weeklyData
    });
  };

  const calculateKwhTrend = (data = gastenverbruik) => {
    const days = {};

    data.forEach(item => {
      const day = moment(item.aankomst).format('YYYY-MM-DD');
      if (!days[day]) {
        days[day] = { date: day, kwh: 0, count: 0 };
      }
      // Calculate kWh as meterstandvertrek - meterstanndaankomst
      const kwh = (item.meterstandvertrek && item.meterstanndaankomst)
        ? (item.meterstandvertrek - item.meterstanndaankomst)
        : 0;
      days[day].kwh += kwh;
      days[day].count += 1;
    });

    return Object.values(days).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const calculateMonthlyData = (data = gastenverbruik) => {
    const months = {};

    data.forEach(item => {
      const month = moment(item.aankomst).format('YYYY-MM');
      const monthName = moment(item.aankomst).format('MMM YYYY');

      if (!months[month]) {
        months[month] = {
          period: monthName,
          reservations: 0,
          kwh: 0,
          revenue: 0
        };
      }
      months[month].reservations += 1;
      // Calculate kWh as meterstandvertrek - meterstanndaankomst
      const kwh = (item.meterstandvertrek && item.meterstanndaankomst)
        ? (item.meterstandvertrek - item.meterstanndaankomst)
        : 0;
      months[month].kwh += kwh;

      // Calculate revenue: kWh * price per kWh
      const prijsPerKwh = item.gast?.gastenprofiel?.prijs ||
                         item.standplaats?.categorie?.prijs ||
                         item.accommodaties?.categorie?.prijs || 0;
      months[month].revenue += kwh * prijsPerKwh;
    });

    return Object.values(months).sort((a, b) => moment(a.period, 'MMM YYYY') - moment(b.period, 'MMM YYYY'));
  };

  const calculateWeeklyData = (data = gastenverbruik) => {
    const weeks = {};

    data.forEach(item => {
      const weekStart = moment(item.aankomst).startOf('week');
      const weekKey = weekStart.format('YYYY-WW');
      const weekLabel = `Week ${weekStart.format('WW')} (${weekStart.format('DD MMM')})`;

      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          period: weekLabel,
          reservations: 0,
          kwh: 0,
          revenue: 0
        };
      }
      weeks[weekKey].reservations += 1;
      // Calculate kWh as meterstandvertrek - meterstanndaankomst
      const kwh = (item.meterstandvertrek && item.meterstanndaankomst)
        ? (item.meterstandvertrek - item.meterstanndaankomst)
        : 0;
      weeks[weekKey].kwh += kwh;

      // Calculate revenue: kWh * price per kWh
      const prijsPerKwh = item.gast?.gastenprofiel?.prijs ||
                         item.standplaats?.categorie?.prijs ||
                         item.accommodaties?.categorie?.prijs || 0;
      weeks[weekKey].revenue += kwh * prijsPerKwh;
    });

    return Object.values(weeks).sort((a, b) => {
      const aWeek = moment(a.period.split('(')[1].replace(')', ''), 'DD MMM');
      const bWeek = moment(b.period.split('(')[1].replace(')', ''), 'DD MMM');
      return aWeek - bWeek;
    });
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
      case 'actief':
        return 'badge-success';
      case 'confirmed':
        return 'badge-info';
      case 'pending':
        return 'badge-warning';
      case 'canceled':
      case 'vertrokken':
        return 'badge-secondary';
      default:
        return 'badge-light';
    }
  };

  const getMonthlyChartOptions = () => {
    return {
      chart: {
        type: 'line',
        height: 350,
        toolbar: {
          show: false
        }
      },
      series: [
        {
          name: 'Reserveringen',
          type: 'column',
          data: dashboardData.monthlyData.map(item => item.reservations)
        },
        {
          name: 'kWh',
          type: 'line',
          data: dashboardData.monthlyData.map(item => Math.round(item.kwh))
        },
        {
          name: 'Omzet (€)',
          type: 'line',
          data: dashboardData.monthlyData.map(item => Math.round(item.revenue))
        }
      ],
      xaxis: {
        categories: dashboardData.monthlyData.map(item => item.period),
        labels: {
          style: {
            fontSize: '12px'
          }
        }
      },
      yaxis: [
        {
          title: {
            text: 'Reserveringen'
          }
        },
        {
          opposite: true,
          title: {
            text: 'kWh / Omzet'
          }
        }
      ],
      colors: ['#1f77b4', '#ff7f0e', '#2ca02c'],
      legend: {
        position: 'top'
      },
      stroke: {
        width: [0, 2, 2]
      },
      fill: {
        opacity: [0.85, 1, 1]
      }
    };
  };

  const getWeeklyChartOptions = () => {
    return {
      chart: {
        type: 'line',
        height: 350,
        toolbar: {
          show: false
        }
      },
      series: [
        {
          name: 'Reserveringen',
          type: 'column',
          data: dashboardData.weeklyData.map(item => item.reservations)
        },
        {
          name: 'kWh',
          type: 'line',
          data: dashboardData.weeklyData.map(item => Math.round(item.kwh))
        },
        {
          name: 'Omzet (€)',
          type: 'line',
          data: dashboardData.weeklyData.map(item => Math.round(item.revenue))
        }
      ],
      xaxis: {
        categories: dashboardData.weeklyData.map(item => item.period.split('(')[0].trim()),
        labels: {
          style: {
            fontSize: '10px'
          },
          rotate: -45
        }
      },
      yaxis: [
        {
          title: {
            text: 'Reserveringen'
          }
        },
        {
          opposite: true,
          title: {
            text: 'kWh / Omzet'
          }
        }
      ],
      colors: ['#1f77b4', '#ff7f0e', '#2ca02c'],
      legend: {
        position: 'top'
      },
      stroke: {
        width: [0, 2, 2]
      },
      fill: {
        opacity: [0.85, 1, 1]
      }
    };
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="content-header">
            <h1 className="content-header-title">
              <i className="fas fa-chart-line mr-3"></i>
              Gastenverbruik Dashboard
            </h1>
            <div className="content-header-subtitle">
              Analyse en overzicht van gastenverbruik gegevens
            </div>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-3">
                  <label className="form-label">Van datum:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.startDate}
                    onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tot datum:</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.endDate}
                    onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Gastenprofiel:</label>
                  <select
                    className="form-control"
                    value={selectedGastenprofiel}
                    onChange={(e) => setSelectedGastenprofiel(e.target.value)}
                  >
                    <option value="">Alle gastenprofielen</option>
                    {gastenprofielen.map((profiel, index) => (
                      <option key={index} value={profiel}>{profiel}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <button
                    className="btn btn-primary"
                    onClick={loadDashboardData}
                    disabled={loading}
                  >
                    <i className="fas fa-sync-alt mr-2"></i>
                    Vernieuwen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-primary shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    Totaal Reserveringen
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {dashboardData.totalReservations.toLocaleString()}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-calendar fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-success shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Totaal kWh
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {dashboardData.totalKwh.toFixed(2)} kWh
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-bolt fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-info shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                    Totale Omzet
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {formatCurrency(dashboardData.totalRevenue)}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-euro-sign fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-warning shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    Gem. kWh/Reservering
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {dashboardData.avgKwhPerReservation.toFixed(1)} kWh
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-chart-bar fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Top Standplaatsen */}
        <div className="col-xl-6 col-lg-6">
          <div className="card shadow mb-4">
            <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
              <h6 className="m-0 font-weight-bold text-primary">Top 5 Standplaatsen</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Standplaats</th>
                      <th>Reserveringen</th>
                      <th>Totaal kWh</th>
                      <th>Omzet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.topStandplaatsen.map((item, index) => (
                      <tr key={index}>
                        <td>{item.name}</td>
                        <td>{item.count}</td>
                        <td>{item.kwh.toFixed(2)} kWh</td>
                        <td>{formatCurrency(item.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Status Distribution */}
        <div className="col-xl-6 col-lg-6">
          <div className="card shadow mb-4">
            <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
              <h6 className="m-0 font-weight-bold text-primary">Status Verdeling</h6>
            </div>
            <div className="card-body">
              {Object.entries(dashboardData.statusDistribution).map(([status, count]) => (
                <div key={status} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-capitalize">{status}</span>
                    <span className={`badge ${getStatusBadgeClass(status)}`}>{count}</span>
                  </div>
                  <div className="progress">
                    <div
                      className="progress-bar"
                      style={{
                        width: `${(count / dashboardData.totalReservations) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Analysis */}
      <div className="row">
        <div className="col-xl-6 col-lg-6">
          <div className="card shadow mb-4">
            <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
              <h6 className="m-0 font-weight-bold text-primary">Maandelijkse Analyse</h6>
            </div>
            <div className="card-body">
              {dashboardData.monthlyData.length > 0 ? (
                <Chart
                  options={getMonthlyChartOptions()}
                  series={getMonthlyChartOptions().series}
                  type="line"
                  height={350}
                />
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-chart-line fa-3x mb-3"></i>
                  <p>Geen maandelijkse data beschikbaar voor de geselecteerde periode</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Weekly Analysis */}
        <div className="col-xl-6 col-lg-6">
          <div className="card shadow mb-4">
            <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
              <h6 className="m-0 font-weight-bold text-primary">Wekelijkse Analyse</h6>
            </div>
            <div className="card-body">
              {dashboardData.weeklyData.length > 0 ? (
                <Chart
                  options={getWeeklyChartOptions()}
                  series={getWeeklyChartOptions().series}
                  type="line"
                  height={350}
                />
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-chart-line fa-3x mb-3"></i>
                  <p>Geen wekelijkse data beschikbaar voor de geselecteerde periode</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
              <h6 className="m-0 font-weight-bold text-primary">Recente Activiteit</h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Gast</th>
                      <th>Standplaats</th>
                      <th>kWh</th>
                      <th>Status</th>
                      <th>Datum</th>
                      <th>Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.recentActivity.map((item) => (
                      <tr key={item.id}>
                        <td>{item.gast}</td>
                        <td>{item.keuze}</td>
                        <td>{item.kwh.toFixed(2)} kWh</td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                            {item.status}
                          </span>
                        </td>
                        <td>{moment(item.datum).format('DD-MM-YYYY HH:mm')}</td>
                        <td>
                          <a href={`/gastenverbruik/edit/${item.id}`} className="btn btn-sm btn-primary">
                            <i className="fas fa-eye"></i>
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GastenVerbruikDashboard;